import React from "react";
import ExcelJS from "exceljs";
import { parseCustomDate } from "./ExcelVettingReport";
import moment from "moment";

const ExcelDeviationTakeSubReport = ({ data }) => {
    const exportExcelFile = async () => {
        if (!Array.isArray(data)) {
            console.error("Data is not an array:", data);
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        // Define columns with headers
        worksheet.columns = [
            { header: 'S.No', key: 'serial', width: 10 },
            { header: 'File Name', key: 'file_no', width: 15 },
            { header: 'Login Date&Time', key: 'login_date', width: 30 },
            { header: 'Process Officer Name', key: 'po_name', width: 30 },
            { header: 'Deviation Officer Name', key: 'deviation_init_userName', width: 30 },
            { header: 'Bank Name', key: 'bank_name', width: 50 },
            { header: 'Product Name', key: 'product', width: 20 },
            { header: 'Bank Branch Name', key: 'bank_branch_name', width: 20 },
            { header: 'LH Branch Name', key: 'lh_branch', width: 20 },
            { header: 'Borrower Name', key: 'borrower_name', width: 30 },
            { header: 'Owner Name', key: 'owner_name', width: 30 },
            { header: 'App ID', key: 'app_id', width: 30 },
            { header: 'File Status', key: 'file_status', width: 30 },
            { header: 'Deviation Requested by', key: 'requestedBy', width: 30 },
            { header: 'Name', key: 'deviation_req_userName', width: 30 },
            { header: 'Mobile No', key: 'phoneNo', width: 20 },
            { header: 'Designation', key: 'designation', width: 30 },
            { header: 'Deviation Request Reason', key: 'requestReason', width: 30 },
            { header: 'Doubt Location', key: 'doubtLocation', width: 30 },
            { header: 'Deviation Taken', key: 'deviationTaken', width: 30 },
            { header: 'Deviation Summary', key: 'summary', width: 30 },
            { header: 'Knowledge Assessment Mark', key: 'knowledgeAssessment', width: 30 },
            { header: 'Training Needed', key: 'trainingNeeded', width: 30 },
            { header: 'Training to whom', key: 'trainingToWhom', width: 30 },
            { header: 'Training Area', key: 'trainingArea', width: 30 },
            { header: 'Special File Need Training for process Team', key: 'specialFile', width: 30 },
            { header: 'File Picked Date&Time', key: 'createdDt', width: 30 },
            { header: 'File Completed Date&Time', key: 'summaryAt', width: 30 },
            { header: 'Total Time', key: 'working_time', width: 30 },
        ];

        // Apply alignment and color to header cells
        worksheet.getRow(1).eachCell((cell) => {
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFF00' }, // Yellow color
            };
            cell.font = { bold: true, size: 14 };
        });

        const flattenedData = data.map((report, index) => {
            // Keep dates as strings in the original format
            // Don't convert to Date objects - ExcelJS handles string dates better
            const createdDt = report.createdDt;
            const summaryAt = report.summaryAt;
            
            // Calculate working time in seconds
            let workingTimeSeconds = null;
            if (createdDt && summaryAt) {
                const startTime = moment(createdDt, "DD-MM-YYYY HH:mm:ss");
                const endTime = moment(summaryAt, "DD-MM-YYYY HH:mm:ss");
                
                if (startTime.isValid() && endTime.isValid()) {
                    workingTimeSeconds = endTime.diff(startTime, 'seconds');
                }
            }

            return {
                ...report,
                login_date: report.login_date,
                createdDt: createdDt,
                summaryAt: summaryAt,
                working_time: workingTimeSeconds, // Keep as seconds
                serial: report.serial_no || index + 1 // Add serial number
            };
        });

        flattenedData.forEach((item) => {
            const row = worksheet.addRow(item);
            row.eachCell((cell, colNumber) => {
                if ([1, 22].includes(colNumber)) { // S.No and Knowledge Assessment Mark
                    cell.numFmt = '0';
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                } 
                else if ([6].includes(colNumber)) { // Bank Name
                    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                } 
                else if ([3, 27, 28].includes(colNumber)) { // Date columns
                    // Keep the date as a string in the original format
                    if (cell.value) {
                        // Ensure it's displayed as text with the correct format
                        cell.numFmt = '@'; // Text format
                        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                    } else {
                        cell.value = '';
                    }
                } 
                else if ([29].includes(colNumber)) { // Total Time column
                    if (cell.value !== null && !isNaN(cell.value)) {
                        // Convert seconds to HH:MM:SS format as a string
                        const hours = Math.floor(cell.value / 3600);
                        const minutes = Math.floor((cell.value % 3600) / 60);
                        const seconds = cell.value % 60;
                        
                        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                        cell.value = timeString;
                        cell.numFmt = '@'; // Text format
                        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                    } else {
                        cell.value = '';
                    }
                }
                else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                }
            });
        });

        const currentDate = new Date();
        const formattedDate = `${currentDate.getDate().toString().padStart(2, '0')}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getFullYear()}`;
        
        // Generate Excel file buffer
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `Deviation Tracker Subreport ${formattedDate}.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <>
            <button className="excel-download-btn" onClick={exportExcelFile}>
                Download as Excel
            </button>
        </>
    );
};

export default ExcelDeviationTakeSubReport;
